/**
 * Semantic overlay layers.
 *
 * Single source of truth for overlay stacking. The keys are the Tailwind token
 * names, so `OVERLAY_LAYERS.dialog` (the imperative number, still needed by
 * Mantine's numeric `zIndex` props) and `z-dialog` (the class) can never drift:
 *
 * - `tailwind.config.ts` builds `theme.extend.zIndex` from this object;
 * - `cn()` registers these token names with tailwind-merge, so a call site can
 *   still override a primitive's layer with `className="z-[9999]"`;
 * - `Z_INDICES` in `components/constants.tsx` derives its Mantine values here.
 *
 * Domain code must not escalate stacking with arbitrary `z-[9999]` values. When
 * a new stacked combination is needed, add a named role here and document which
 * surfaces it must clear.
 *
 * See `docs/architecture/dialog-architecture-and-mantine-migration.md`
 * ("Z-index and portals").
 *
 * Toast is intentionally absent: since the sonner migration, toast layering is
 * owned by sonner itself, which pins its viewport at `z-index: 999999999` —
 * above everything here by design.
 *
 * Known debt: `apps/viteapp/src/styles/global.css` forces every
 * `[data-radix-popper-content-wrapper]` to `z-index: 700 !important` (and to
 * `800 !important` when it contains a listbox). That override currently wins
 * over the `menu`, `popover` and `tooltip` values below whenever the surface is
 * rendered through a Radix popper. The values here are kept at their historical
 * numbers so introducing this module changed no stacking behavior; untangling
 * the `!important` rules is separate work that needs browser verification.
 */
export const OVERLAY_LAYERS = {
  /** Sticky page chrome: sidebar, sticky table headers. */
  'page-sticky': 30,
  /** Tooltip content. */
  tooltip: 30,
  /** Sheet and drawer backdrop and content. */
  sheet: 50,
  /** Dropdown, select, context menu and menubar content anchored to the page. */
  menu: 50,
  /** Dialog backdrop and content. */
  dialog: 700,
  /** Alert dialog backdrop and content — must clear an ordinary dialog. */
  'alert-dialog': 800,
  /** Popover content that must clear an alert dialog. */
  popover: 1000,
  /** Sheet opened from a Popover while its parent remains mounted. */
  'popover-child-sheet': 1001,

  /**
   * Radix dialog opened from a z-1000 legacy Mantine modal. This also clears
   * that parent's z-1001 controls.
   */
  'legacy-modal-child-dialog': 1002,
  /** Body-level portal owned by a dialog at `legacy-modal-child-dialog`. */
  'legacy-modal-child-portal': 1003,

  /** The Help box beside the sidebar: above sheets and menus, below dialogs. */
  'support-hub': 600,
  /** Clears legacy charge modals, whose content uses layer 10001. */
  'guide-spotlight': 10004,
  /** An in-app guide's bubble: above its own spotlight. */
  'guide-bubble': 10005,

  /** @deprecated Mantine drawer. Remove with the Mantine modals migration. */
  'legacy-drawer': 200,
  /** @deprecated Mantine modal opened above a Radix dialog host. */
  'legacy-nested-dialog': 750,
  /** @deprecated Mantine menu layered above Mantine modals. */
  'legacy-menu': 800,
  /** @deprecated Mantine select/date-picker/menu portals. */
  'legacy-portal': 1001,
} as const;

export type OverlayLayer = keyof typeof OVERLAY_LAYERS;

/** Token names as used in class names, e.g. `z-dialog`. */
export const OVERLAY_LAYER_TOKENS = Object.keys(
  OVERLAY_LAYERS,
) as OverlayLayer[];
